// src/modules/Beneficiaries.js
import * as React from 'react';
import {
  Datagrid,
  List,
  TextField,
  DateField,
  BooleanField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  ReferenceInput,
  BooleanInput,
  DateInput,
  NumberInput,
  Create,
  Edit,
  SimpleForm,
  required,
  usePermissions,
  useListContext,
  downloadCSV,
  useRecordContext,
  Button,
  useUpdate,
  useNotify,
  useRefresh,
  ChipField,
  SingleFieldList,
  ArrayField,
  SelectArrayInput,
  ReferenceArrayInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  Show,
  ShowButton,
  DeleteButton,
  EditButton
} from "react-admin";
import { Fragment, useState } from "react";
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  Stack, 
  TablePagination,
  Box,
  Typography,
  Chip,
  Card,
  CardContent
} from "@mui/material";
import { unparse as convertToCSV } from 'papaparse';
import jsonExport from 'jsonexport/dist';

// Custom List Actions without external component
const BeneficiaryListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

// Define a custom filter component for beneficiaries
export const BeneficiariesFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
    <TextInput label="Phone Number" source="phonenumber" />
    <SelectInput
      source="gender"
      choices={[
        { id: "MALE", name: "Male" },
        { id: "FEMALE", name: "Female" },
        { id: "OTHER", name: "Other" },
      ]}
      optionText="name"
      optionValue="id"
      allowEmpty
    />
    <NumberInput source="state_id" label="State ID" />
    <NumberInput source="locality_id" label="Locality ID" />
    <TextInput label="Field Coordinator" source="fc_name" />
    <BooleanInput source="is_registered" label="Is Registered" />
    <BooleanInput source="is_active" label="Is Active" />
    <DateInput source="fromDate" label="Created From" />
    <DateInput source="toDate" label="Created To" />
  </Filter>
);

const customExporter = (records) => {
  const csvData = records.map(record => ({
    ID: record.id,
    Name: record.name,
    PhoneNumber: record.phonenumber ? `'${record.phonenumber}` : "",
    StateID: record.state_id,
    LocalityID: record.locality_id,
    Gender: record.gender,
    DateOfBirth: record.dob ? new Date(record.dob).toLocaleDateString() : "",
    FieldCoordinator: record.fc_name || "",
    Crops: record.crops || "",
    CreatedAt: record.created_at ? new Date(record.created_at).toLocaleDateString() : "",
    UpdatedAt: record.updated_at ? new Date(record.updated_at).toLocaleDateString() : "",
    IsRegistered: record.is_registered ? "Yes" : "No",
    RegisteredUserID: record.registered_user_id || "",
    IsActive: record.is_active ? "Yes" : "No",
  }));

  jsonExport(csvData, { rowDelimiter: ',' }, (err, csv) => {
    if (err) {
      console.error(err);
      return;
    }
    const bom = '\uFEFF';
    downloadCSV(bom + csv, 'beneficiaries_export');
  });
};

const CustomPagination = (props) => {
  const { total, perPage, page, setPerPage, setPage } = useListContext();

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <TablePagination
      count={total}
      page={page - 1}
      rowsPerPage={perPage}
      onPageChange={(event, newPage) => setPage(newPage + 1)}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={[10, 25, 50, 100]}
      component="div"
    />
  );
};

// Status Chip Component
const StatusChip = ({ is_active, is_registered }) => {
  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {is_active ? (
        <Chip 
          label="Active" 
          size="small" 
          color="success" 
          variant="outlined"
        />
      ) : (
        <Chip 
          label="Inactive" 
          size="small" 
          color="error" 
          variant="outlined"
        />
      )}
      {is_registered && (
        <Chip 
          label="Registered" 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )}
    </Box>
  );
};

// Age Calculation Function
const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Quick Actions Component
const QuickActions = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();

  const handleStatusToggle = async (newStatus) => {
    try {
      await update(
        'beneficiaries',
        { id: record.id, data: { is_active: newStatus } },
        {
          onSuccess: () => {
            notify(`Beneficiary ${newStatus ? 'activated' : 'deactivated'} successfully`, { type: 'success' });
            refresh();
          },
          onError: (error) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  if (!record || !['admin', 'operater'].includes(permissions)) return null;

  return (
    <Box display="flex" gap={1}>
      {record.is_active ? (
        <Button
          label="Deactivate"
          size="small"
          color="warning"
          onClick={() => handleStatusToggle(false)}
          disabled={isLoading}
        />
      ) : (
        <Button
          label="Activate"
          size="small"
          color="success"
          onClick={() => handleStatusToggle(true)}
          disabled={isLoading}
        />
      )}
    </Box>
  );
};

export const BeneficiariesList = (props) => {
  const { permissions } = usePermissions();
  const canEdit = ['admin', 'manager', 'operater'].includes(permissions);
  
  return (
    <List
      {...props}
      storeKey={false}
      filters={<BeneficiariesFilter />}
      actions={<BeneficiaryListActions />}
      pagination={<CustomPagination />}
      exporter={customExporter}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      <Datagrid
        rowClick={canEdit ? "edit" : "show"}
        bulkActionButtons={false}
        sx={{
          "& .RaBulkActionsToolbar-toolbar": {
            display: "none",
          },
          "& .column-age": {
            minWidth: 60,
          },
          "& .column-status": {
            minWidth: 120,
          },
          "& .column-actions": {
            minWidth: 150,
          },
        }}
      >
        <TextField source="name" label="Name" />
        <TextField source="phonenumber" label="Phone" />
        <TextField source="gender" label="Gender" />
        <FunctionField
          label="Age"
          render={(record) => calculateAge(record.dob)}
          className="column-age"
        />
        <TextField source="state_id" label="State ID" />
        <TextField source="locality_id" label="Locality ID" />
        <TextField source="fc_name" label="Field Coordinator" />
        <FunctionField
          label="Status"
          render={(record) => (
            <StatusChip 
              is_active={record.is_active}
              is_registered={record.is_registered}
            />
          )}
          className="column-status"
        />
        <DateField source="created_at" label="Created" showTime />
        {/* <FunctionField
          label="Actions"
          render={(record) => <QuickActions record={record} />}
          className="column-actions"
        /> */}
        <ShowButton />
        {canEdit && <EditButton />}
      </Datagrid>
    </List>
  );
};

// Dynamic form fields for create/edit
const BeneficiaryDynamicFields = () => {
  return (
    <Fragment>
      <TextInput 
        source="name" 
        label="Name" 
        fullWidth 
        validate={[required()]} 
      />
      <TextInput 
        source="phonenumber" 
        label="Phone Number" 
        fullWidth 
        validate={[required()]} 
      />
      <SelectInput
        source="gender"
        label="Gender"
        choices={[
          { id: "MALE", name: "Male" },
          { id: "FEMALE", name: "Female" },
          { id: "OTHER", name: "Other" },
        ]}
        fullWidth
        validate={[required()]}
      />
      <DateInput 
        source="dob" 
        label="Date of Birth" 
        fullWidth 
      />
      <NumberInput 
        source="state_id" 
        label="State ID" 
        fullWidth 
        validate={[required()]} 
      />
      <NumberInput 
        source="locality_id" 
        label="Locality ID" 
        fullWidth 
      />
      <TextInput 
        source="fc_name" 
        label="Field Coordinator Name" 
        fullWidth 
      />
      <TextInput 
        source="crops" 
        label="Crops (Text)" 
        fullWidth 
        multiline 
        rows={3}
      />
      <BooleanInput 
        source="is_active" 
        label="Is Active" 
        fullWidth 
      />
    </Fragment>
  );
};

// Create form for beneficiaries
export const BeneficiaryCreate = (props) => {
  const { permissions } = usePermissions();
  const canCreate = ['admin', 'operater'].includes(permissions);

  const transform = (data) => {
    const transformed = {
      ...data,
      state_id: Number(data.state_id),
      locality_id: data.locality_id ? Number(data.locality_id) : null,
      dob: data.dob ? new Date(data.dob).toISOString().split('T')[0] : null,
      is_active: data.is_active || false,
      is_registered: false,
    };
    return transformed;
  };

  return canCreate ? (
    <Create {...props} transform={transform}>
      <SimpleForm>
        <BeneficiaryDynamicFields />
      </SimpleForm>
    </Create>
  ) : undefined;
};

// Edit form for beneficiaries
export const BeneficiaryEdit = (props) => {
  const { permissions } = usePermissions();
  const canEdit = ['admin', 'manager'].includes(permissions);

  const transform = (data) => {
    const transformed = {
      ...data,
      state_id: Number(data.state_id),
      locality_id: data.locality_id ? Number(data.locality_id) : null,
      dob: data.dob ? new Date(data.dob).toISOString().split('T')[0] : null,
    };
    
    // Don't include phone number in updates
    delete transformed.phonenumber;
    
    return transformed;
  };

  return canEdit ? (
    <Edit {...props} transform={transform}>
      <SimpleForm>
        <BeneficiaryDynamicFields />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Registration Information
        </Typography>
        <BooleanInput 
          source="is_registered" 
          label="Is Registered" 
          disabled 
        />
        {props.record?.is_registered && (
          <TextField 
            source="registered_user_id" 
            label="Registered User ID" 
            disabled 
          />
        )}
      </SimpleForm>
    </Edit>
  ) : undefined;
};

// Show view for beneficiaries
export const BeneficiaryShow = (props) => {
  return (
    <Show {...props} title="Beneficiary Details">
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Basic Information
              </Typography>
              <Typography variant="body1"><strong>Name:</strong> {props.record?.name}</Typography>
              <Typography variant="body1"><strong>Phone:</strong> {props.record?.phonenumber}</Typography>
              <Typography variant="body1"><strong>Gender:</strong> {props.record?.gender}</Typography>
              <Typography variant="body1"><strong>Date of Birth:</strong> {props.record?.dob ? new Date(props.record.dob).toLocaleDateString() : 'N/A'}</Typography>
              <Typography variant="body1"><strong>Age:</strong> {calculateAge(props.record?.dob)} years</Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                Location Information
              </Typography>
              <Typography variant="body1"><strong>State ID:</strong> {props.record?.state_id}</Typography>
              <Typography variant="body1"><strong>Locality ID:</strong> {props.record?.locality_id || 'N/A'}</Typography>
              <Typography variant="body1"><strong>Field Coordinator:</strong> {props.record?.fc_name || 'N/A'}</Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                Crops Information
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>Crops:</strong> {props.record?.crops || 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                Status Information
              </Typography>
              <StatusChip 
                is_active={props.record?.is_active}
                is_registered={props.record?.is_registered}
              />
              {props.record?.is_registered && (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>Registered User ID:</strong> {props.record?.registered_user_id}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                Timestamps
              </Typography>
              <Typography variant="body1"><strong>Created:</strong> {props.record?.created_at ? new Date(props.record.created_at).toLocaleString() : 'N/A'}</Typography>
              <Typography variant="body1"><strong>Last Updated:</strong> {props.record?.updated_at ? new Date(props.record.updated_at).toLocaleString() : 'N/A'}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Show>
  );
};

// Simple version if you want to quickly test
export const SimpleBeneficiariesList = (props) => {
  return (
    <List {...props}>
      <Datagrid>
        <TextField source="name" />
        <TextField source="phonenumber" />
        <TextField source="gender" />
        <TextField source="state_id" />
        <BooleanField source="is_active" />
        <DateField source="created_at" />
        <ShowButton />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const ListActions = ({ filters, ...props }) => {
  return (
    <TopToolbar>
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box>
          {filters && React.cloneElement(filters, { context: 'button' })}
        </Box>
        <Box>
          <CreateButton />
          <ExportButton />
        </Box>
      </Box>
    </TopToolbar>
  );
};